import type { VisitorSessionResponseDto } from '@nomiss/sdk';
import { executeSdkRawRequest } from './api-request.util';
import { onboardingApi } from './sdk-client';

export type VisitorSessionStatus = {
  visitorId: string;
  status: VisitorSessionResponseDto['status'] | string;
  expiresAt: string | null;
};

function normalizeExpiresAt(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  return null;
}

export type VisitorSessionLookup =
  | { result: 'found'; session: VisitorSessionStatus }
  | { result: 'missing' }
  | { result: 'unavailable' };

function isMissingVisitorSessionError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const status = (error as { status?: unknown }).status;
  return status === 403 || status === 404;
}

/**
 * Distinguishes a dead cached id (`403` / `404`) from a transient failure.
 * Login drops missing sessions. Network and `5xx` stay `unavailable` so the
 * cached id is kept.
 */
export async function lookupVisitorSession(
  visitorId: string,
): Promise<VisitorSessionLookup> {
  try {
    const data = await executeSdkRawRequest<VisitorSessionResponseDto>({
      request: () =>
        onboardingApi.onboardingControllerGetVisitorSessionV1Raw({
          xVisitorId: visitorId,
        }),
      fallback: 'Failed to fetch visitor session',
    });

    return {
      result: 'found',
      session: {
        visitorId: data.visitorId,
        status: data.status,
        expiresAt: normalizeExpiresAt(data.expiresAt),
      },
    };
  } catch (error) {
    if (isMissingVisitorSessionError(error)) {
      return { result: 'missing' };
    }

    return { result: 'unavailable' };
  }
}

export async function fetchVisitorSessionStatus(
  visitorId: string,
): Promise<VisitorSessionStatus | null> {
  const lookup = await lookupVisitorSession(visitorId);
  return lookup.result === 'found' ? lookup.session : null;
}
